import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { contentBriefApi, ContentBrief, CreateContentBriefRequest, GeneratedContentBriefSuggestion, postIdeaApi, PostIdea } from '../services/api';
import { PageHeader, useConfirm, EmptyState, LoadingState, RelatedLinks, useFocusParam } from '../components/ui';
import { ideaBacklinks } from '../linkedin/ideaLinks';
import { FlowStepBar, useLinkedInFlow } from '../linkedin';
import { routeById } from '../navigation/routes';

const ContentBriefPage: React.FC = () => {
  const { confirm } = useConfirm();
  const { refresh } = useLinkedInFlow();
  const [searchParams] = useSearchParams();
  const presetIdeaId = searchParams.get('postIdeaId');
  const [contentBriefs, setContentBriefs] = useState<ContentBrief[]>([]);
  const [postIdeas, setPostIdeas] = useState<PostIdea[]>([]);
  const [selectedBrief, setSelectedBrief] = useState<ContentBrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [aiProvider, setAiProvider] = useState<'openai' | 'claude' | 'perplexity'>('claude');
  const [formData, setFormData] = useState<CreateContentBriefRequest>({
    postIdeaId: '',
    theme: '',
    challenge: '',
    turningPoint: '',
    learning: '',
    fullBrief: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  // Per Query kommt die Idee mit (Serien-Workflow, Sprung-Chips): hat sie
  // schon einen Brief, wird er geoeffnet, sonst das Formular dafuer.
  useFocusParam('postIdeaId', !loading);
  useEffect(() => {
    if (!presetIdeaId || loading) return;
    const vorhanden = contentBriefs.find((b) => b.postIdeaId === presetIdeaId);
    if (vorhanden) {
      setSelectedBrief(vorhanden);
      return;
    }
    setFormData((f) => (f.postIdeaId ? f : { ...f, postIdeaId: presetIdeaId }));
    setShowForm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetIdeaId, loading]);

  const loadData = async () => {
    try {
      setLoading(true);
      const ideas = (await postIdeaApi.getAll()) ?? [];
      setPostIdeas(ideas);

      // Die Ideen bringen ihren Brief mit -- kein Einzelabruf je Idee mehr.
      const briefs = ideas
        .filter((idea) => idea.contentBrief)
        .map((idea) => ({ ...(idea.contentBrief as ContentBrief), postIdea: idea }));
      setContentBriefs(briefs);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Kurzbeschreibungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (editingId) {
        await contentBriefApi.update(editingId, formData);
      } else {
        await contentBriefApi.create(formData);
      }
      await loadData();
      refresh();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const handleGenerateAI = async () => {
    if (!formData.postIdeaId) {
      setError('Bitte zuerst eine Beitragsidee auswählen');
      return;
    }

    setError('');
    setGenerating(true);

    try {
      const suggestion: GeneratedContentBriefSuggestion = await contentBriefApi.generate({
        postIdeaId: formData.postIdeaId,
        provider: aiProvider,
      });

      setFormData({
        ...formData,
        theme: suggestion.theme,
        challenge: suggestion.challenge,
        turningPoint: suggestion.turningPoint || '',
        learning: suggestion.learning,
        fullBrief: suggestion.fullBrief || '',
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler bei der KI-Generierung. Bitte erneut versuchen.');
    } finally {
      setGenerating(false);
    }
  };

  const handleEdit = (brief: ContentBrief) => {
    setEditingId(brief.id);
    setFormData({
      postIdeaId: brief.postIdeaId,
      theme: brief.theme,
      challenge: brief.challenge,
      turningPoint: brief.turningPoint || '',
      learning: brief.learning,
      fullBrief: brief.fullBrief || '',
    });
    setShowForm(true);
    setSelectedBrief(brief);
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({ title: 'Content Brief löschen?', confirmLabel: 'Löschen', tone: 'danger' });
    if (!ok) return;

    try {
      await contentBriefApi.delete(id);
      setSelectedBrief(null);
      await loadData();
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const resetForm = () => {
    setFormData({
      postIdeaId: '',
      theme: '',
      challenge: '',
      turningPoint: '',
      learning: '',
      fullBrief: '',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getAvailablePostIdeas = () => {
    const briefPostIdeaIds = new Set(contentBriefs.map(b => b.postIdeaId));
    return postIdeas.filter(idea => !briefPostIdeaIds.has(idea.id) || idea.id === formData.postIdeaId);
  };

  if (loading) {
    return <div className="page-container"><FlowStepBar stepId="briefs" /><LoadingState /></div>;
  }

  return (
    <div className="page-container">
      <FlowStepBar stepId="briefs" />
      <PageHeader
        title="Beitragsentwürfe (Content Briefs)"
        subtitle={routeById('li-briefs')?.description}
        actions={(
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn btn-primary"
          >
            {showForm ? 'Abbrechen' : '+ Neue Kurzbeschreibung'}
          </button>
        )}
      />

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="info-panel info-panel-primary">
        <div className="info-panel-title">Tipp:</div>
        Eine Kurzbeschreibung strukturiert deine Beitragsidee mit 4 Elementen:
        <ul className="mt-2 mb-0">
          <li><strong>Thema:</strong> Worum geht es? (1 Satz)</li>
          <li><strong>Herausforderung:</strong> Welches Problem wird adressiert? (1-2 Sätze)</li>
          <li><strong>Wendepunkt:</strong> Was ist der entscheidende Moment/Erkenntnis? (1-2 Sätze)</li>
          <li><strong>Learning:</strong> Was ist die Kernbotschaft? (1-2 Sätze)</li>
        </ul>
      </div>

      {postIdeas.length === 0 && (
        <EmptyState
          compact
          icon="lightbulb"
          title="Noch keine Beitragsideen"
          description="Ein Brief gehört immer zu einer Beitragsidee. Lege zuerst Ideen an."
          action={{ label: 'Zu den Beitragsideen', to: '/post-ideas' }}
        />
      )}

      {showForm && (
        <div className="form-section">
          <h2 className="form-section-title">{editingId ? 'Kurzbeschreibung bearbeiten' : 'Neue Kurzbeschreibung erstellen'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">
                Beitragsidee auswählen *
              </label>
              <select
                value={formData.postIdeaId}
                onChange={(e) => setFormData({ ...formData, postIdeaId: e.target.value })}
                required
                disabled={!!editingId}
                className="form-select"
              >
                <option value="">Bitte wählen...</option>
                {getAvailablePostIdeas().map((idea) => (
                  <option key={idea.id} value={idea.id}>
                    {idea.title} ({idea.topic})
                  </option>
                ))}
              </select>
              {editingId && (
                <small className="form-helper">
                  Die Beitragsidee kann nach dem Erstellen nicht mehr geändert werden
                </small>
              )}
            </div>

            {/* AI Generation Section */}
            <div className="info-panel info-panel-primary" style={{ padding: '16px', marginBottom: '16px' }}>
              <div className="info-panel-title" style={{ marginBottom: '8px' }}>KI-Unterstützung</div>
              <p className="text-sm" style={{ marginBottom: '12px' }}>
                Lass die KI automatisch alle 4 Elemente der Kurzbeschreibung basierend auf der ausgewählten Beitragsidee generieren. Du kannst die Vorschläge anschließend anpassen.
              </p>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={aiProvider}
                  onChange={(e) => setAiProvider(e.target.value as 'openai' | 'claude' | 'perplexity')}
                  className="form-select"
                  style={{ width: 'auto', minWidth: '160px' }}
                  disabled={generating}
                >
                  <option value="claude">Claude (Anthropic)</option>
                  <option value="openai">GPT-4 (OpenAI)</option>
                  <option value="perplexity">Perplexity</option>
                </select>
                <button
                  type="button"
                  onClick={handleGenerateAI}
                  disabled={!formData.postIdeaId || generating}
                  className="btn btn-primary"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {generating ? 'Generiere...' : 'Mit KI generieren'}
                </button>
              </div>
              {!formData.postIdeaId && (
                <small className="form-helper" style={{ marginTop: '8px', display: 'block' }}>
                  Bitte zuerst eine Beitragsidee auswählen, um die KI-Generierung zu nutzen
                </small>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Thema * <span className="form-label-hint">(Worum geht es?)</span>
              </label>
              <input
                type="text"
                value={formData.theme}
                onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                required
                placeholder="z.B. Die Bedeutung klarer Kommunikation im Remote-Team"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Herausforderung * <span className="form-label-hint">(Welches Problem wird adressiert?)</span>
              </label>
              <textarea
                value={formData.challenge}
                onChange={(e) => setFormData({ ...formData, challenge: e.target.value })}
                required
                placeholder="z.B. Remote-Teams verlieren oft wichtige Informationen durch unklare Kommunikation..."
                rows={3}
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Wendepunkt <span className="form-label-hint">(Was ist der entscheidende Moment/Erkenntnis?)</span>
              </label>
              <textarea
                value={formData.turningPoint}
                onChange={(e) => setFormData({ ...formData, turningPoint: e.target.value })}
                placeholder="z.B. Die Einführung eines strukturierten Daily-Standups veränderte alles..."
                rows={3}
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Learning * <span className="form-label-hint">(Was ist die Kernbotschaft?)</span>
              </label>
              <textarea
                value={formData.learning}
                onChange={(e) => setFormData({ ...formData, learning: e.target.value })}
                required
                placeholder="z.B. Klare Kommunikationsregeln sind wichtiger als perfekte Tools"
                rows={3}
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Vollständiger Brief <span className="form-label-hint">(Optional - zusammenfassender Fließtext)</span>
              </label>
              <textarea
                value={formData.fullBrief}
                onChange={(e) => setFormData({ ...formData, fullBrief: e.target.value })}
                placeholder="Zusammenfassender Fließtext der alle 4 Elemente verbindet..."
                rows={5}
                className="form-textarea"
              />
            </div>

            <div className="btn-group">
              <button
                type="submit"
                className="btn btn-success"
              >
                {editingId ? 'Aktualisieren' : 'Erstellen'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn btn-secondary"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <div>
        <h2>Alle Kurzbeschreibungen ({contentBriefs.length})</h2>
        {contentBriefs.length === 0 ? (
          <EmptyState
            icon="file-text"
            title="Noch keine Beitragsentwürfe"
            description="Wähle eine Beitragsidee und lass dir Thema, Herausforderung, Wendepunkt und Learning vorschlagen."
            action={postIdeas.length > 0 ? { label: 'Ersten Brief erstellen', onClick: () => setShowForm(true) } : undefined}
          />
        ) : (
          <div className="grid-cards-2">
            {contentBriefs.map((brief) => (
              <div
                key={brief.id}
                data-focus-id={brief.postIdeaId}
                onClick={() => setSelectedBrief(brief)}
                className={`card card-interactive${selectedBrief?.id === brief.id ? ' card-active' : ''}`}
                style={selectedBrief?.id === brief.id ? { borderColor: 'var(--color-primary)', borderWidth: '2px' } : undefined}
              >
                <div className="card-header">
                  <h3 className="card-title">
                    {brief.postIdea?.title || 'Beitragsidee'}
                  </h3>
                </div>

                {(brief.postIdea?.strategy || brief.postIdea?.seriesFormat) && (
                  <div className="mb-4 flex gap-2 flex-wrap">
                    {brief.postIdea?.strategy && (
                      <span className="badge badge-info" style={{ fontSize: '10px' }}>
                        {brief.postIdea.strategy.name}
                      </span>
                    )}
                    {brief.postIdea?.seriesFormat && (
                      <span className="badge badge-neutral" style={{ fontSize: '10px' }}>
                        {brief.postIdea.seriesFormat.name}
                      </span>
                    )}
                  </div>
                )}

                <div className="mb-4">
                  <strong className="text-sm">Thema:</strong>
                  <p className="text-sm mt-1">{brief.theme}</p>
                </div>

                <div className="mb-4">
                  <strong className="text-sm">Herausforderung:</strong>
                  <p className="text-xs text-secondary mt-1">
                    {brief.challenge.substring(0, 100)}{brief.challenge.length > 100 ? '...' : ''}
                  </p>
                </div>

                {brief.turningPoint && (
                  <div className="mb-4">
                    <strong className="text-sm">Wendepunkt:</strong>
                    <p className="text-xs text-secondary mt-1">
                      {brief.turningPoint.substring(0, 100)}{brief.turningPoint.length > 100 ? '...' : ''}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <strong className="text-sm">Learning:</strong>
                  <p className="text-xs text-secondary mt-1">
                    {brief.learning.substring(0, 100)}{brief.learning.length > 100 ? '...' : ''}
                  </p>
                </div>

                <div className="card-footer">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(brief);
                    }}
                    className="btn btn-primary btn-sm"
                  >
                    Bearbeiten
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(brief.id);
                    }}
                    className="btn btn-danger btn-sm"
                  >
                    Löschen
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail View */}
      {selectedBrief && !showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2 className="modal-title">{selectedBrief.postIdea?.title}</h2>
              <button
                onClick={() => setSelectedBrief(null)}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <RelatedLinks className="mb-4" items={ideaBacklinks(selectedBrief.postIdeaId, { omit: ['brief'] })} label="Verknüpft mit dieser Idee" />

            <div className="info-panel info-panel-primary mb-6">
              <strong>Beitragsidee:</strong> {selectedBrief.postIdea?.topic}
              {selectedBrief.postIdea?.strategy && (
                <div className="mt-2">
                  <strong>Strategie:</strong> {selectedBrief.postIdea.strategy.name} ({selectedBrief.postIdea.strategy.industry} / {selectedBrief.postIdea.strategy.targetAudience})
                </div>
              )}
              {selectedBrief.postIdea?.seriesFormat && (
                <div className="mt-2">
                  <strong>Serienformat:</strong> {selectedBrief.postIdea.seriesFormat.name}
                </div>
              )}
            </div>

            <div className="mb-6">
              <h3>Thema</h3>
              <p>{selectedBrief.theme}</p>
            </div>

            <div className="mb-6">
              <h3>Herausforderung</h3>
              <p>{selectedBrief.challenge}</p>
            </div>

            {selectedBrief.turningPoint && (
              <div className="mb-6">
                <h3>Wendepunkt</h3>
                <p>{selectedBrief.turningPoint}</p>
              </div>
            )}

            <div className="mb-6">
              <h3>Learning</h3>
              <p>{selectedBrief.learning}</p>
            </div>

            {selectedBrief.fullBrief && (
              <div className="mb-6">
                <h3>Vollständiger Brief</h3>
                <div className="code-block">
                  {selectedBrief.fullBrief}
                </div>
              </div>
            )}

            <div className="btn-group mt-6">
              <button
                onClick={() => {
                  setSelectedBrief(null);
                  handleEdit(selectedBrief);
                }}
                className="btn btn-primary"
              >
                Bearbeiten
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentBriefPage;
